/**
 * Site-wide cookie consent banner.
 *
 * UX rules (compliance-critical — do not "improve" without rereading):
 *   - Two buttons, equal visual weight. ePrivacy / CNIL / EDPB
 *     guidance: "Reject" must be as easy as "Accept". No "Manage"
 *     trapdoor that disguises rejection. No emotional / punitive
 *     framing on the reject button.
 *   - Hidden when consent is already set; shown again only if the
 *     user explicitly resets it (via a future footer link).
 *   - Mobile-first: bottom sheet that doesn't cover the user's
 *     primary action above; on wider screens, a centered card with a
 *     comfortable max-width.
 *
 * Copy is intentionally specific about which tools load on accept —
 * "informed" is one of the four legal qualities of valid consent under
 * GDPR Art. 4(11). Generic "we use cookies" copy doesn't meet that bar.
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { useConsent } from './useConsent';

const ConsentBanner: React.FC = () => {
  const { consent, accept, reject } = useConsent();

  if (consent !== 'unset') return null;

  // Brand teal — same hue used in MapHero and the comparison chart. Pinned
  // here as a literal rather than as a Tailwind token because the value is
  // duplicated across several files and a token-renaming pass is out of
  // scope (see Item C in the calculator backlog).
  const brandTeal = 'hsl(174, 70%, 43%)';

  return (
    <div
      role="dialog"
      aria-modal="false"
      aria-label="Cookie consent"
      className="fixed inset-x-0 bottom-0 z-50 px-2 pb-2 sm:px-3 sm:pb-3 pointer-events-none"
    >
      <div
        className="pointer-events-auto mx-auto max-w-3xl rounded-lg bg-white shadow-lg ring-1 ring-slate-200 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 px-3 py-2.5 sm:px-4 sm:py-3"
        style={{
          borderLeft: `3px solid ${brandTeal}`,
          paddingBottom: 'max(0.625rem, env(safe-area-inset-bottom))',
        }}
      >
        <p className="text-xs sm:text-sm text-slate-700 leading-snug m-0 flex-1">
          <span className="font-semibold text-slate-900">Help us make Jody better.</span>{' '}
          With your OK, we'll switch on two optional extras: Hotjar, so you can
          share quick feedback right on the page, and Google's tag, which helps
          us see which ads bring fellow travellers our way. Prefer not to? No
          problem.{' '}
          <Link
            to="/privacy-policy"
            className="underline hover:text-slate-900 whitespace-nowrap"
          >
            Privacy Policy
          </Link>
        </p>
        <div className="flex gap-2 sm:flex-shrink-0">
          <button
            type="button"
            onClick={reject}
            className="flex-1 sm:flex-none inline-flex items-center justify-center rounded border border-slate-300 bg-white px-3 py-1.5 text-xs sm:text-sm font-medium text-slate-700 hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-slate-400"
          >
            Reject
          </button>
          <button
            type="button"
            onClick={accept}
            className="flex-1 sm:flex-none inline-flex items-center justify-center rounded px-3 py-1.5 text-xs sm:text-sm font-medium text-white hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-1"
            style={{ backgroundColor: brandTeal }}
          >
            Accept
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConsentBanner;
